import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PaEmptyState } from "components/platform-admin";
import {
  buildPlanRevenueChartData,
  buildPlanTenantChartData,
  buildCollectedTimelineChartData,
  buildRevenueHealthChartData,
  buildTenantMixChartData,
  buildTrialExpiryChartData,
  FINANCIAL_CHART_COLORS,
  formatCurrency,
  TENANT_MIX_COLORS,
} from "./billingOverview.utils";

const ChartCard = ({ title, description, children }) => (
  <article className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    {description ? <p className="mt-1 text-xs text-gray-500">{description}</p> : null}
    <div className="mt-3 h-64">{children}</div>
  </article>
);

const EmptyChart = ({ message }) => (
  <div className="flex h-full items-center justify-center">
    <PaEmptyState title="No chart data" message={message} />
  </div>
);

const renderPieLabel = ({ name, percent }) =>
  percent >= 0.08 ? `${name} ${(percent * 100).toFixed(0)}%` : "";

const AnalyticsSection = ({ title, description, children }) => (
  <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className="mb-5">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
    </div>
    {children}
  </section>
);

const FinancialOverviewCharts = ({ billing }) => {
  const currency = billing?.estimated_mrr?.currency || "AUD";

  const tenantMix = useMemo(
    () => buildTenantMixChartData(billing?.stats),
    [billing]
  );
  const planTenants = useMemo(
    () => buildPlanTenantChartData(billing?.plan_breakdown),
    [billing]
  );
  const planRevenue = useMemo(
    () => buildPlanRevenueChartData(billing?.plan_breakdown, currency),
    [billing, currency]
  );
  const trialTimeline = useMemo(
    () => buildTrialExpiryChartData(billing?.trials_expiring),
    [billing]
  );
  const collectedTimeline = useMemo(
    () => buildCollectedTimelineChartData(billing?.revenue_summary),
    [billing]
  );
  const revenueHealth = useMemo(
    () => buildRevenueHealthChartData(billing?.revenue_summary),
    [billing]
  );
  const revenueSummary = billing?.revenue_summary;

  return (
    <div className="space-y-6">
      {revenueSummary ? (
        <AnalyticsSection
          title="Revenue trends"
          description="Cash collected over time and recent collections compared to open invoice balances."
        >
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <ChartCard
              title="Monthly collected revenue"
              description="Paid invoice totals grouped by month from synced Stripe records."
            >
              {collectedTimeline.length === 0 ? (
                <EmptyChart message="No collected revenue recorded yet." />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={collectedTimeline} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => formatCurrency(value, currency)}
                    />
                    <Tooltip
                      formatter={(value, name, props) => [
                        props?.payload?.collected_label || formatCurrency(Number(value), currency),
                        "Collected",
                      ]}
                    />
                    <Bar dataKey="collected_cents" fill="#10b981" radius={[4, 4, 0, 0]} name="Collected" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard
              title="Collected vs outstanding (30 days)"
              description="Recent collections compared to open invoice balances."
            >
              {revenueHealth.length === 0 ? (
                <EmptyChart message="No revenue activity in the last 30 days." />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueHealth} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => formatCurrency(value * 100, currency)}
                    />
                    <Tooltip
                      formatter={(value, name, props) => [
                        formatCurrency((props?.payload?.raw_cents ?? value * 100), currency),
                        name,
                      ]}
                    />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} name="Amount">
                      {revenueHealth.map((entry, index) => (
                        <Cell key={entry.name} fill={index === 0 ? "#10b981" : "#ef4444"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>
        </AnalyticsSection>
      ) : null}

      <AnalyticsSection
        title="Portfolio & pipeline"
        description="Tenant billing mix, plan distribution, recurring revenue estimates, and upcoming trial expiries."
      >
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <ChartCard
            title="Tenant billing mix"
            description="Paying, trial, failed, and other billing states."
          >
            {tenantMix.length === 0 ? (
              <EmptyChart message="No tenant billing data available." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tenantMix}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={2}
                    label={renderPieLabel}
                  >
                    {tenantMix.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={TENANT_MIX_COLORS[entry.name] || FINANCIAL_CHART_COLORS[0]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} tenants`, name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard
            title="Trials expiring (7 days)"
            description="Upcoming trial end dates."
          >
            {trialTimeline.length === 0 ? (
              <EmptyChart message="No trials expiring in the next 7 days." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trialTimeline} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [`${value} tenants`, "Expiring"]} />
                  <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Tenants" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard
            title="Tenants by plan"
            description="Paying, trial, and failed tenants per assigned plan."
          >
            {planTenants.length === 0 ? (
              <EmptyChart message="No plan assignment data." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={planTenants} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="plan"
                    tick={{ fontSize: 11 }}
                    interval={0}
                    angle={-15}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Paying" stackId="plan" fill="#4f46e5" />
                  <Bar dataKey="Trial" stackId="plan" fill="#f59e0b" />
                  <Bar dataKey="Failed" stackId="plan" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard
            title="Estimated MRR by plan"
            description="Monthly recurring revenue from paying tenants matched to catalog prices."
          >
            {planRevenue.length === 0 ? (
              <EmptyChart message="MRR estimates require Stripe catalog prices and paying tenants." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={planRevenue}
                  layout="vertical"
                  margin={{ top: 8, right: 16, left: 16, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => formatCurrency(value, currency)}
                  />
                  <YAxis type="category" dataKey="plan" width={110} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value, name, props) => [
                      props?.payload?.revenue_label || formatCurrency(Number(value), currency),
                      "Est. MRR",
                    ]}
                    labelFormatter={(label) => `Plan: ${label}`}
                  />
                  <Bar dataKey="revenue_cents" fill="#10b981" radius={[0, 4, 4, 0]} name="Est. MRR">
                    {planRevenue.map((entry, index) => (
                      <Cell
                        key={entry.plan}
                        fill={FINANCIAL_CHART_COLORS[index % FINANCIAL_CHART_COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      </AnalyticsSection>
    </div>
  );
};

export default FinancialOverviewCharts;
